import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/useApp.js';
import { 
  ArrowLeft,
  Clock,
  Calendar,
  Check,
  Save,
  X
} from 'lucide-react';

const LawyerSetAvailability = () => {
  const navigate = useNavigate();
  const { language, user } = useApp();
  const [selectedDays, setSelectedDays] = useState([]);
  const [timeSlots, setTimeSlots] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const translations = {
    english: {
      setAvailability: 'Set Availability',
      back: 'Back',
      availabilityDaysTimeSlots: 'Availability (days & time slots)',
      selectAvailableDays: 'Select your available days and time slots for consultations',
      availableDays: 'Available Days',
      monday: 'Monday',
      tuesday: 'Tuesday',
      wednesday: 'Wednesday',
      thursday: 'Thursday',
      friday: 'Friday',
      saturday: 'Saturday',
      sunday: 'Sunday',
      timeSlots: 'Time Slots',
      addTimeSlot: 'Add Time Slot',
      removeTimeSlot: 'Remove Time Slot',
      startTime: 'Start Time',
      endTime: 'End Time',
      save: 'Save',
      saving: 'Saving...',
      saved: 'Saved Successfully!',
      error: 'Error saving availability',
      selectDays: 'Please select at least one day',
      selectTimeSlots: 'Please add time slots for selected days'
    },
    hindi: {
      setAvailability: 'उपलब्धता सेट करें',
      back: 'वापस',
      availabilityDaysTimeSlots: 'उपलब्धता (दिन और समय स्लॉट)',
      selectAvailableDays: 'परामर्श के लिए अपने उपलब्ध दिन और समय स्लॉट चुनें',
      availableDays: 'उपलब्ध दिन',
      monday: 'सोमवार',
      tuesday: 'मंगलवार',
      wednesday: 'बुधवार',
      thursday: 'गुरुवार',
      friday: 'शुक्रवार',
      saturday: 'शनिवार',
      sunday: 'रविवार',
      timeSlots: 'समय स्लॉट',
      addTimeSlot: 'समय स्लॉट जोड़ें',
      removeTimeSlot: 'समय स्लॉट हटाएं',
      startTime: 'शुरू का समय',
      endTime: 'समाप्ति का समय',
      save: 'सहेजें',
      saving: 'सहेज रहे हैं...',
      saved: 'सफलतापूर्वक सहेजा गया!',
      error: 'उपलब्धता सहेजने में त्रुटि',
      selectDays: 'कृपया कम से कम एक दिन चुनें',
      selectTimeSlots: 'कृपया चयनित दिनों के लिए समय स्लॉट जोड़ें'
    }
  };

  const t = translations[language] || translations.english;

  const daysOfWeek = [
    { key: 'monday', label: t.monday },
    { key: 'tuesday', label: t.tuesday },
    { key: 'wednesday', label: t.wednesday },
    { key: 'thursday', label: t.thursday },
    { key: 'friday', label: t.friday },
    { key: 'saturday', label: t.saturday },
    { key: 'sunday', label: t.sunday }
  ];

  // Load existing availability data
  useEffect(() => {
    try {
      const existingAvailability = JSON.parse(localStorage.getItem('lawyer_availability') || '{}');
      if (existingAvailability.selectedDays) {
        setSelectedDays(existingAvailability.selectedDays);
      }
      if (existingAvailability.timeSlots) {
        setTimeSlots(existingAvailability.timeSlots);
      }
    } catch (error) {
      console.error('Error loading availability:', error);
    }
  }, []);

  const toggleDay = (day) => {
    setSelectedDays(prev => {
      if (prev.includes(day)) {
        // Remove day and its time slots
        const newTimeSlots = { ...timeSlots };
        delete newTimeSlots[day];
        setTimeSlots(newTimeSlots);
        return prev.filter(d => d !== day);
      } else {
        // Add day with default time slot
        const newTimeSlots = {
          ...timeSlots,
          [day]: [{ id: `${day}-1`, startTime: '09:00', endTime: '17:00' }]
        };
        setTimeSlots(newTimeSlots);
        return [...prev, day];
      }
    });
  };

  const addTimeSlot = (day) => {
    const daySlots = timeSlots[day] || [];
    const newSlot = {
      id: `${day}-${Date.now()}`,
      startTime: '09:00',
      endTime: '17:00'
    };
    setTimeSlots(prev => ({
      ...prev,
      [day]: [...daySlots, newSlot]
    }));
  };

  const removeTimeSlot = (day, slotId) => {
    const daySlots = timeSlots[day] || [];
    const updatedSlots = daySlots.filter(slot => slot.id !== slotId);
    
    if (updatedSlots.length === 0) {
      // Remove day if no time slots left
      setSelectedDays(prev => prev.filter(d => d !== day));
      const newTimeSlots = { ...timeSlots };
      delete newTimeSlots[day];
      setTimeSlots(newTimeSlots);
    } else {
      setTimeSlots(prev => ({
        ...prev,
        [day]: updatedSlots
      }));
    }
  };

  const updateTimeSlot = (day, slotId, field, value) => {
    setTimeSlots(prev => ({
      ...prev,
      [day]: prev[day].map(slot =>
        slot.id === slotId ? { ...slot, [field]: value } : slot
      )
    }));
  };

  const handleSave = async () => {
    if (selectedDays.length === 0) {
      alert(t.selectDays);
      return;
    }

    // Validate time slots
    for (const day of selectedDays) {
      if (!timeSlots[day] || timeSlots[day].length === 0) {
        alert(t.selectTimeSlots);
        return;
      }
    }

    setIsSaving(true);
    
    try {
      const availabilityData = {
        selectedDays,
        timeSlots,
        lastUpdated: new Date().toISOString()
      };
      
      localStorage.setItem('lawyer_availability', JSON.stringify(availabilityData));
      
      // Show success message
      setTimeout(() => {
        setIsSaving(false);
        alert(t.saved);
        navigate('/lawyer-settings');
      }, 1000);
    } catch (error) {
      console.error('Error saving availability:', error);
      setIsSaving(false);
      alert(t.error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-lavender-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-6"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/lawyer-settings')}
                className="w-10 h-10 bg-white dark:bg-gray-800 rounded-lg flex items-center justify-center shadow-sm hover:shadow-md transition-all duration-200"
              >
                <ArrowLeft className="w-5 h-5 text-purple-500" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {t.setAvailability}
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t.availabilityDaysTimeSlots}
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Description */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-6"
        >
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
            <p className="text-blue-800 dark:text-blue-200 text-sm">
              {t.selectAvailableDays}
            </p>
          </div>
        </motion.div>

        {/* Available Days */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 mb-6"
        >
          <div className="p-6 border-b border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/20 rounded-lg flex items-center justify-center">
                <Calendar className="w-4 h-4 text-purple-500" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t.availableDays}
              </h3>
            </div>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {daysOfWeek.map((day) => {
                const isSelected = selectedDays.includes(day.key);
                
                return (
                  <button
                    key={day.key}
                    onClick={() => toggleDay(day.key)}
                    className={`p-4 rounded-lg border-2 transition-all duration-200 flex items-center justify-center ${
                      isSelected
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300'
                        : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-purple-300 dark:hover:border-purple-600'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {isSelected && <Check className="w-4 h-4" />}
                      <span className="font-medium">{day.label}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </motion.div>

        {/* Time Slots for Selected Days */}
        {selectedDays.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="space-y-6"
          >
            {selectedDays.map((day) => {
              const dayLabel = daysOfWeek.find(d => d.key === day)?.label;
              const daySlots = timeSlots[day] || [];
              
              return (
                <div
                  key={day}
                  className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700"
                >
                  <div className="p-6 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
                          <Clock className="w-4 h-4 text-green-500" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {dayLabel} - {t.timeSlots}
                        </h3>
                      </div>
                      <button
                        onClick={() => addTimeSlot(day)}
                        className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors duration-200 text-sm font-medium"
                      >
                        {t.addTimeSlot}
                      </button>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <div className="space-y-4">
                      {daySlots.map((slot, index) => (
                        <div
                          key={slot.id}
                          className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg"
                        >
                          <div className="flex-1 grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                {t.startTime}
                              </label>
                              <input
                                type="time"
                                value={slot.startTime}
                                onChange={(e) => updateTimeSlot(day, slot.id, 'startTime', e.target.value)}
                                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                {t.endTime}
                              </label>
                              <input
                                type="time"
                                value={slot.endTime}
                                onChange={(e) => updateTimeSlot(day, slot.id, 'endTime', e.target.value)}
                                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                              />
                            </div>
                          </div>
                          <button
                            onClick={() => removeTimeSlot(day, slot.id)}
                            className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors duration-200"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </motion.div>
        )}

        {/* Save Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-8"
        >
          <button
            onClick={handleSave}
            disabled={isSaving || selectedDays.length === 0}
            className={`w-full py-4 px-6 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center gap-3 ${
              isSaving || selectedDays.length === 0
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-purple-500 hover:bg-purple-600 text-white shadow-lg hover:shadow-xl'
            }`}
          >
            {isSaving ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {t.saving}
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                {t.save}
              </>
            )}
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default LawyerSetAvailability;
